import React, { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as jose from 'jose'
import t from '../../../../translations/main'
import storage from 'src/components/Core/Utils/storage'
import { getBaseApiHost, getEndpointUrl } from 'src/components/Core/Utils/url_constants'
import { useApplicationSelected, useProspectsApplications } from 'src/components/Core/Context/OnboardingContext'
import { openFileNewTab, filePayloadFactory } from 'src/components/Core/Utils/global'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'

import InputFiles from 'src/components/Atoms/InputFiles/InputFiles'
import Button from 'src/components/Atoms/Button/Button'
import Font from 'src/components/Core/Typography/Font/Font'

import { SendRequestType } from 'src/components/Core/Hooks/useFetch/types'
import { FilePayloadFactoryType, InputFilesGroupProps } from './types'
import { InputFilesGroupWrapper } from './InputFilesGroup.styles'
import { OnChangeType } from 'src/components/Atoms/InputFiles/types'
import { ApiData } from 'src/views/Pages/Reports/Statements/types'

// Map from https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
// .csv -> text/csv
// .doc -> application/msword
// .docx -> application/vnd.openxmlformats-officedocument.wordprocessingml.document
// .jpg, .jpeg -> image/jpeg
// .odt -> application/vnd.oasis.opendocument.text
// .pdf -> application/pdf
// .png -> image/png
// .ppt -> application/vnd.ms-powerpoint
// .pptx -> application/vnd.openxmlformats-officedocument.presentationml.presentation
// .tif, .tiff -> image/tiff
// .txt -> text/plain
// .xls -> application/vnd.ms-excel
// .xlsx -> application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
const MIME_FILE_TYPES = 'text/csv, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, application/vnd.oasis.opendocument.text, application/pdf, image/png, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, image/tiff, text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export const flowKeyMap: Record<string, string> = {
  IndividualAccount: 'IndividualAOF',
  DeliverableAccount: 'DeliverableAOF',
  CorporateAccount: 'CorpAOF'
}
// eslint-disable-next-line
const download = (data: any, fileName: string) => {
  const link = document.createElement('a')
  // @ts-ignore
  link.href = data
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
}

export const downloadPreview = (sendRequest: SendRequestType, flow: string , applicationId: number, documentId: string, name: string) => {
  if(!documentId || !name) return
  sendRequest(
    {
      scope: 'onboarding',
      url: getEndpointUrl(`${flow}/downloadDocument`, applicationId, 'postMessage.json', `DocumentID=${documentId}`)
    },
    data => {
      if(data?.documentBase64) openFileNewTab(data.documentBase64)
    }
  )
}

const token: string = storage?.get(storage?.keys?.main?.token)
//@ts-ignore:next-line
const userRole: string = token && jose?.decodeJwt(token)?.extension_UserStatus

const isEmployee = userRole === 'Employee'

const InputFilesGroup: FC<InputFilesGroupProps> = props => {
  const {
    fetchOptions,
    inputLabel,
    buttonLabel,
    singleFileInput,
    gridArea,
    groupLabel,
    margin,
    inputMargin,
    value,
    name,
    labelAsTextInput,
    labelAsTextInputPlaceholder,
    labelAsTextInputLabel,
    labelForInputFile,
    width,
    removeBorder,
    groupLabelMargin,
    customOnDelete,
    customOnChange,
    required,
    itemKey,
    numberOfFiles,
    dynamicNumberOfFiles,
    flowKeyProp,
    removeAddButton,
    removeRemoveButton,
    disabled,
    noFilterOutName,
    isSignature,
    scanForVirusInfo
  } = props
  const [rowInputFiles, setRowInputFiles] = useState(!singleFileInput ? [] : [0])
  const [rowInputFileNames, setRowInputFileNames] = useState<FilePayloadFactoryType[]>()
  const [lastElId, setLastElId] = useState<number | undefined>(0)
  const [lastCallId, setLastCall] = useState()

  const navigate = useNavigate()
  const { flowKey } = useParams()

  const prospectsApplications = useProspectsApplications()
  const applicationSelected = useApplicationSelected()
  const { sendRequest, isLoading, error } = useFetch()

  const isDocumentation = prospectsApplications[`${applicationSelected}`].permission === 'documentation'
  const applicationId = storage.get(storage?.keys?.onboarding?.applicationId)
  const flow = flowKeyMap[`${flowKey || flowKeyProp}`]

  const sendFile = async (file: File, inputTextName?: string, callback?: (data: ApiData) => void, downloadFileInfoID?: string) => {
    try {
      sendRequest(
        {
          scope: 'onboarding',
          url: fetchOptions.requestConfig.url,
          method: fetchOptions.requestConfig.method || 'POST',
          headers: {
            'Content-Type':  fetchOptions.requestConfig.headers?.contentType || 'application/json',
          },
          // @ts-ignore:next-line
          body: await filePayloadFactory(file, props.step, name, applicationId, inputTextName, isSignature, downloadFileInfoID),
          ...fetchOptions.requestConfig
        },
        data => {
          if(!data.reasonPhrase){
            fetchOptions?.applyData?.(data)
            callback?.(data)
          }
        }

      )
    } catch (error) {
      console.error('Error uploading file, error: ', error)
    }
  }

  const deleteFile = (documentID: string, callback?: () => void) => {
    const url = isSignature
      ? getEndpointUrl('Signature/removeSignedFile', applicationId, 'postMessage.json',`UploadedFileID=${documentID}`)
      : `${getBaseApiHost()}/${flow}/RemoveDocument?applicationId=${applicationId}&DocumentID=${documentID}`
    try {
      sendRequest(
        {
          scope: 'onboarding',
          url: import.meta.env.VITE_MOCK_DATA !== 'true'
            ? url
            : `${window.location.origin}/mock/deleteFile.json`,
          method: isSignature ? 'POST' : 'DELETE',
        },
        fetchOptions.applyData || callback || (() => {
          return null
        })
      )
    } catch (error) {
      console.error('Error deleting file, error: ', error)
    }
    !noFilterOutName && setRowInputFileNames(prev => {
      return prev.filter(file => file.documentID !== parseInt(documentID))
    })
  }

  const downloadOrPreviewFile = (fileid?: string, filename?: string, isPreview?: boolean) => {
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl('Signature/downloadSignatureFile', applicationId, 'postMessage.json',`fileid=${fileid}`),
        method: 'POST'
      },
      data => {
        isPreview ? openFileNewTab(data) : download(data, filename)
      }

    )
  }

  const inputFileOnChange = (file: OnChangeType, callback?: (data: ApiData) => void, downloadFileInfoID?: string): null => {
    if(!file?.data) return null

    sendFile(file.data, file.inputTextName, callback, downloadFileInfoID)
  }

  const deleteRow = (id: number, documentID?: string) => {
    const indexToRemove = rowInputFiles.findIndex(item => item === id)
    const temp = [...rowInputFiles]
    temp.splice(indexToRemove, 1)
    setRowInputFiles(temp)
    customOnChange({value: {removeRow: true, indexOfFile: indexToRemove}, name})
    // Check if the input holds a file, if it holds then on remove the input also call the endpoint to delete the file on server, if not then it just removes the input
    documentID && deleteFile(documentID)
  }

  useEffect(() => {
    if (!fetchOptions?.requestConfig?.url || !applicationId) {
      navigate('/error/internalProblem')
    }

    if (rowInputFileNames || value === undefined) return

    if (Array.isArray(value)) {
      // @ts-ignore: next-line
      setRowInputFileNames(value)
      // eslint-disable-next-line
      setRowInputFiles(value.map((_e: any, index: number) => index))
    } else {
      setRowInputFileNames([value as FilePayloadFactoryType])
      setRowInputFiles([0])
    }
  }, [value])

  useEffect(() => {
    // @ts-ignore: next-line
    if(numberOfFiles > 0 && dynamicNumberOfFiles){
      setRowInputFiles([...Array(numberOfFiles).keys()])
    }

  }, [numberOfFiles])

  useEffect(() => {
    if(lastElId < rowInputFiles[`${rowInputFiles.length - 1}`]){
      setLastElId(rowInputFiles.length > 0 ? rowInputFiles[`${rowInputFiles.length - 1}`] : 0)
    }
  },[rowInputFiles])

  return (
    <InputFilesGroupWrapper
      key={props.itemKey}
      {...props.flex}
      gridArea={gridArea}
      margin={margin}
      width={width}
      disabled={disabled}
      isDocumentation={isDocumentation || isEmployee}
    >
      { groupLabel && <Font ellipsis={false} bold={false} margin={groupLabelMargin} >{groupLabel}</Font> }
      {
        // @ts-ignore:next-line
        rowInputFileNames?.some(file => file.documentFileExtension || file.uploadedFileID) &&
        isDocumentation &&
        <Font ellipsis={false} bold margin={groupLabelMargin} >{t('onboarding.main.docPreviewText')} {isLoading && t('onboarding.main.gettingPreview')}</Font>
      }
      {
        rowInputFiles.length > 0 && rowInputFiles.map((numberOnArray, index) => {
          return (
            <InputFiles
              key={numberOnArray}
              name={name}
              required={required}
              width='100%'
              margin={inputMargin}
              // @ts-ignore:next-line
              label={rowInputFileNames?.[`${numberOnArray}`]?.documentLabel || rowInputFileNames?.[`${numberOnArray}`]?.fileLabel || inputLabel}
              labelAsTextInput={!inputLabel && labelAsTextInput}
              labelAsTextInputPlaceholder={labelAsTextInputPlaceholder}
              labelAsTextInputLabel={labelAsTextInputLabel}
              labelForInputFile={labelForInputFile}
              fileInputId={numberOnArray}
              onChange={(file, callback, downloadFileInfoID) => {
                if(!file?.data) return null
                inputFileOnChange(file, data => {
                  callback(data)
                  // @ts-ignore
                  props?.customSignatureOnChange?.(file, numberOnArray, data)
                  // @ts-ignore
                  customOnChange?.({value: {...file, visualKey: itemKey, indexOfFile: index, documentID: parseInt(data) }, name})
                }, downloadFileInfoID)
                // @ts-ignore
                setLastCall(numberOnArray)
              }}
              deleteFile={deleteFile}
              serverFileName={
                rowInputFileNames?.[`${numberOnArray}`]
                  ? {
                    // @ts-ignore:next-line
                    name: rowInputFileNames[`${numberOnArray}`].documentName || rowInputFileNames[`${numberOnArray}`].uploadedFileName,
                    // @ts-ignore:next-line
                    documentID: isSignature ? rowInputFileNames[`${numberOnArray}`]?.uploadedFileID?.toString() : rowInputFileNames[`${numberOnArray}`]?.documentID?.toString()
                  }
                  : {name: ''}
              }
              labelAsTextInputServerValue={labelAsTextInput && rowInputFileNames?.[`${numberOnArray}`]?.documentFileType}
              removeBorder={removeBorder}
              downloadOnlyFileInfo={
                {
                  // @ts-ignore:next-line
                  name: rowInputFileNames?.[`${numberOnArray}`]?.downloadFileName,
                  // @ts-ignore:next-line
                  downloadFileID:rowInputFileNames?.[`${numberOnArray}`]?.downloadFileID,
                }
              }
              customOnDelete={customOnDelete}
              customOnChange={customOnChange}
              error={error && lastCallId === numberOnArray ? error : null}
              deleteRow={deleteRow}
              singleFileInput={singleFileInput}
              removeRemoveButton={removeRemoveButton}
              downloadFile={downloadOrPreviewFile}
              accept={MIME_FILE_TYPES}
              downloadPreviewClick={isSignature ? downloadOrPreviewFile : (docId, name) => downloadPreview(sendRequest, flow, applicationId, docId, name)}
              isDocumentation={isDocumentation}
              isEmployee={isEmployee}
              scanForVirusInfo={scanForVirusInfo}
            />
          )
        })
      }
      {!singleFileInput && !removeAddButton && <Button
      // @ts-ignore
        onClick={event => {
          event.stopPropagation()
          event.preventDefault()
          customOnChange({value: {message: 'add-input-file', numberOfRows: rowInputFiles.length + 1}, name})
          setRowInputFiles(rowInputFiles.concat((rowInputFiles.length > 0 ? lastElId : 0) + 1))
        }}
        label={buttonLabel || t('components.inputFilesGroup.addInputButtonDefault')}
        version={props.buttonVersion || 4}
        buttonMargin={props.buttonMargin}
        height='100%'
        fontSize={props.fontSize}
      />}
    </InputFilesGroupWrapper>
  )
}

export default InputFilesGroup
